import {
  getLeoUuid,
  LeoUUID,
  getString,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { ChatSurveyMutationResult, ChatSurveyMutationResultDTO } from "./chatSurveyMutationResult";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
/* eslint-disable import/export */
export abstract class SaveChatSurveyAnswerRPC implements
  LeoRPC<SaveChatSurveyAnswerRPC.Request, SaveChatSurveyAnswerRPC.Response, SaveChatSurveyAnswerRPC.Errors.Errors>
{
  abstract execute(
    request: SaveChatSurveyAnswerRPC.Request
  ): Promise<LeoRPCResult<SaveChatSurveyAnswerRPC.Response, SaveChatSurveyAnswerRPC.Errors.Errors>>;
}

export namespace SaveChatSurveyAnswerRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyLinkId: string;
    questionId: string;
    answerJson: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isSaveChatSurveyAnswerRPCRequest: boolean = true;
  
    readonly surveyLinkId: LeoUUID;
    readonly questionId: LeoUUID;
    readonly answerJson: string;
  
  
    constructor(
    surveyLinkId: LeoUUID, 
    questionId: LeoUUID, 
    answerJson: string
    ) {
      this.surveyLinkId = surveyLinkId;
      this.questionId = questionId;
      Request.validateAnswerJson(answerJson);
      this.answerJson = answerJson;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyLinkId = getLeoUuid(dto, "surveyLinkId", InvalidRequestSurveyLinkIdError);
      const questionId = getLeoUuid(dto, "questionId", InvalidRequestQuestionIdError);
      const answerJson = getString(dto, "answerJson", InvalidRequestAnswerJsonError);
      return new Request(
        surveyLinkId,
        questionId,
        answerJson
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyLinkId: this.surveyLinkId.toDTO(),
          questionId: this.questionId.toDTO(),
          answerJson: this.answerJson
      };
      return dto;
    }
    private static validateAnswerJson = (answerJson: string): void => {
      if(answerJson.trim().length === 0) {
        throw new InvalidRequestAnswerJsonNonEmptyError(
          `Attribute answerJson cannot be an empty string.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `SaveChatSurveyAnswerRPC(surveyLinkId='${this.surveyLinkId}',questionId='${this.questionId}',answerJson='${this.answerJson}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    result: ChatSurveyMutationResultDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isSaveChatSurveyAnswerResponse: boolean = true;
  
    readonly result: ChatSurveyMutationResult;
  
  
    constructor(
    result: ChatSurveyMutationResult
    ) {
      this.result = result;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const result = ChatSurveyMutationResult.fromDTO(getObject(dto, "result", InvalidResponseResultError));
      return new Response(
        result
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          result: this.result.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `SaveChatSurveyAnswer(result='${this.result}')`;
  };
  

  export enum RPCError {
    InvalidSurveyLinkId = "INVALID_SURVEY_LINK_ID",
    InvalidQuestionId = "INVALID_QUESTION_ID",
    InvalidAnswerJson = "INVALID_ANSWER_JSON",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    SurveyAlreadySubmitted = "SURVEY_ALREADY_SUBMITTED",
    ChatInterfaceNotEnabled = "CHAT_INTERFACE_NOT_ENABLED",
    QuestionNotAvailable = "QUESTION_NOT_AVAILABLE"
  }

  export namespace Errors {
    
    export interface InvalidSurveyLinkIdDTO {
      code: string;
    }
    
    export class InvalidSurveyLinkId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyLinkId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyLinkId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyLinkIdDTO {
        const dto: InvalidSurveyLinkIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyLinkId.prototype.toString = function toString(): string {
      return `SaveChatSurveyAnswerRPC.INVALID_SURVEY_LINK_ID()`;
    };
    
    
    export interface InvalidQuestionIdDTO {
      code: string;
    }
    
    export class InvalidQuestionId extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionId;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionIdDTO {
        const dto: InvalidQuestionIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionId.prototype.toString = function toString(): string {
      return `SaveChatSurveyAnswerRPC.INVALID_QUESTION_ID()`;
    };
    
    
    export interface InvalidAnswerJsonDTO {
      code: string;
    }
    
    export class InvalidAnswerJson extends LeoRPCError<RPCError> {
      readonly isInvalidAnswerJson: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidAnswerJson;
        super(code);
      }
    
    
      toDTO(): InvalidAnswerJsonDTO {
        const dto: InvalidAnswerJsonDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidAnswerJson.prototype.toString = function toString(): string {
      return `SaveChatSurveyAnswerRPC.INVALID_ANSWER_JSON()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
      surveyIsClosedTitleText: SurveyLocalizedTextDTO;
      surveyIsClosedDescriptionText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
      readonly surveyIsClosedTitleText: SurveyLocalizedText;
      readonly surveyIsClosedDescriptionText: SurveyLocalizedText;
    
      constructor(
      surveyIsClosedTitleText: SurveyLocalizedText, 
      surveyIsClosedDescriptionText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
        this.surveyIsClosedTitleText = surveyIsClosedTitleText;
        this.surveyIsClosedDescriptionText = surveyIsClosedDescriptionText;
      }
    
      static fromDTO(dto: SurveyIsClosedDTO): SurveyIsClosed {
        const surveyIsClosedTitleText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedTitleText", InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError));
        const surveyIsClosedDescriptionText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedDescriptionText", InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError));
        return new SurveyIsClosed(
          surveyIsClosedTitleText,
          surveyIsClosedDescriptionText
        );
      }
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
          surveyIsClosedTitleText: this.surveyIsClosedTitleText.toDTO(),
          surveyIsClosedDescriptionText: this.surveyIsClosedDescriptionText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `SaveChatSurveyAnswerRPC.SURVEY_IS_CLOSED(surveyIsClosedTitleText='${this.surveyIsClosedTitleText}',surveyIsClosedDescriptionText='${this.surveyIsClosedDescriptionText}')`;
    };
    
    
    export interface SurveyAlreadySubmittedDTO {
      code: string;
      thankYouPageText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyAlreadySubmitted extends LeoRPCError<RPCError> {
      readonly isSurveyAlreadySubmitted: boolean = true;
    
      readonly thankYouPageText: SurveyLocalizedText;
    
      constructor(
      thankYouPageText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyAlreadySubmitted;
        super(code);
        this.thankYouPageText = thankYouPageText;
      }
    
      static fromDTO(dto: SurveyAlreadySubmittedDTO): SurveyAlreadySubmitted {
        const thankYouPageText = SurveyLocalizedText.fromDTO(getObject(dto, "thankYouPageText", InvalidResponseSurveyAlreadySubmittedThankYouPageTextError));
        return new SurveyAlreadySubmitted(
          thankYouPageText
        );
      }
    
      toDTO(): SurveyAlreadySubmittedDTO {
        const dto: SurveyAlreadySubmittedDTO = {
          code: this.code,
          thankYouPageText: this.thankYouPageText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyAlreadySubmitted.prototype.toString = function toString(): string {
      return `SaveChatSurveyAnswerRPC.SURVEY_ALREADY_SUBMITTED(thankYouPageText='${this.thankYouPageText}')`;
    };
    
    
    export interface ChatInterfaceNotEnabledDTO {
      code: string;
    }
    
    export class ChatInterfaceNotEnabled extends LeoRPCError<RPCError> {
      readonly isChatInterfaceNotEnabled: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ChatInterfaceNotEnabled;
        super(code);
      }
    
    
      toDTO(): ChatInterfaceNotEnabledDTO {
        const dto: ChatInterfaceNotEnabledDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ChatInterfaceNotEnabled.prototype.toString = function toString(): string {
      return `SaveChatSurveyAnswerRPC.CHAT_INTERFACE_NOT_ENABLED()`;
    };
    
    
    export interface QuestionNotAvailableDTO {
      code: string;
    }
    
    export class QuestionNotAvailable extends LeoRPCError<RPCError> {
      readonly isQuestionNotAvailable: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.QuestionNotAvailable;
        super(code);
      }
    
    
      toDTO(): QuestionNotAvailableDTO {
        const dto: QuestionNotAvailableDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    QuestionNotAvailable.prototype.toString = function toString(): string {
      return `SaveChatSurveyAnswerRPC.QUESTION_NOT_AVAILABLE()`;
    };
    

    export type Errors = InvalidSurveyLinkId | InvalidQuestionId | InvalidAnswerJson | SurveyIsClosed | SurveyAlreadySubmitted | ChatInterfaceNotEnabled | QuestionNotAvailable;
    export type ErrorsDTO = InvalidSurveyLinkIdDTO | InvalidQuestionIdDTO | InvalidAnswerJsonDTO | SurveyIsClosedDTO | SurveyAlreadySubmittedDTO | ChatInterfaceNotEnabledDTO | QuestionNotAvailableDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyLinkId:
          return new InvalidSurveyLinkId();
        case RPCError.InvalidQuestionId:
          return new InvalidQuestionId();
        case RPCError.InvalidAnswerJson:
          return new InvalidAnswerJson();
        case RPCError.SurveyIsClosed:
          return SurveyIsClosed.fromDTO(dto as SurveyIsClosedDTO);
        case RPCError.SurveyAlreadySubmitted:
          return SurveyAlreadySubmitted.fromDTO(dto as SurveyAlreadySubmittedDTO);
        case RPCError.ChatInterfaceNotEnabled:
          return new ChatInterfaceNotEnabled();
        case RPCError.QuestionNotAvailable:
          return new QuestionNotAvailable();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyLinkIdError extends SaveChatSurveyAnswerRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyLinkIdError";
  }
}

export class InvalidRequestQuestionIdError extends SaveChatSurveyAnswerRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionIdError";
  }
}

export class InvalidRequestAnswerJsonError extends SaveChatSurveyAnswerRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestAnswerJsonError";
  }
}

export class InvalidRequestAnswerJsonNonEmptyError extends InvalidRequestAnswerJsonError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestAnswerJsonNonEmptyError";
  }
}

export class InvalidResponseResultError extends SaveChatSurveyAnswerRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseResultError";
  }
}

export class InvalidResponseInvalidSurveyLinkIdError extends SaveChatSurveyAnswerRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyLinkIdError";
  }
}

export class InvalidResponseInvalidQuestionIdError extends SaveChatSurveyAnswerRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionIdError";
  }
}

export class InvalidResponseInvalidAnswerJsonError extends SaveChatSurveyAnswerRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidAnswerJsonError";
  }
}

export class InvalidResponseSurveyIsClosedError extends SaveChatSurveyAnswerRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError";
  }
}
export class InvalidResponseSurveyAlreadySubmittedError extends SaveChatSurveyAnswerRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedError";
  }
}

export class InvalidResponseSurveyAlreadySubmittedThankYouPageTextError extends InvalidResponseSurveyAlreadySubmittedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedThankYouPageTextError";
  }
}
export class InvalidResponseChatInterfaceNotEnabledError extends SaveChatSurveyAnswerRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseChatInterfaceNotEnabledError";
  }
}

export class InvalidResponseQuestionNotAvailableError extends SaveChatSurveyAnswerRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestionNotAvailableError";
  }
}

